import { yupResolver } from '@hookform/resolvers/yup';
import { Card } from '@mantine/core';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { AirlineModel } from 'api-hooks/airline';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import {
  FullGridContainer,
  HalfGridContainer,
  OneQuarterGridContainer,
  OneThirdGridContainer,
} from 'mantine-components/container/grid-container';
import Input from 'mantine-components/elements';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { AirlineFormSchema, AirlineFormType } from './form-type';
import useAirlineDefaultValues from '../hooks/use-airline-default-values';

interface AirlineFormProps {
  query?: UseQueryResult<ApiResult<AirlineModel>, ApiError>;
  onSubmit: (
    input: AirlineFormType,
    form: UseFormReturn<AirlineFormType>,
  ) => Promise<AirlineModel>;
  saveMutation?: UseMutationResult;
}

export default function AirlineForm(props: AirlineFormProps) {
  const { t } = useTranslation();
  const airline = props.query?.data?.data;
  const defaultValues = useAirlineDefaultValues(airline);
  const permission = useGetCrudPermission('airlines');
  const { deleteMutate, onDelete } = useDestroyAction('airlines');

  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);
  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.AirlinesCreate}`);
    if (airline) {
      title = t(`pathname:${NavigationRoutes.AirlinesView}`, {
        extra: `${airline.code} ${airline.name}`,
      });
    }
    titleRef.current?.setTitle(title);
  }, [t, airline]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !airline,
        defaultValues,
        resolver: yupResolver(AirlineFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(airline?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={airline}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <Card withBorder radius={8}>
        <Text variant="headingXSmall">{t('airline:airline_detail')}</Text>
        <SeparatorVertical $margin={8} />
        <HalfGridContainer>
          <Input
            type="text"
            name="code"
            label={t('common:code')}
            placeholder={t('common:code')}
            required
          />
          <Input
            type="text"
            name="name"
            label={t('common:name')}
            placeholder={t('common:name')}
            required
          />
        </HalfGridContainer>
        <SeparatorVertical $margin={8} />
        <OneThirdGridContainer>
          <Input
            type="text"
            name="email"
            label={t('common:email')}
            placeholder={t('common:email')}
            required
          />
          <Input
            type="text"
            name="phone"
            label={t('cruise:tel_phone')}
            placeholder={t('cruise:tel_phone')}
            required
          />
          <Input
            type="text"
            name="phone_2"
            label={t('cruise:cellphone_number')}
            placeholder={t('cruise:cellphone_number')}
          />
        </OneThirdGridContainer>
        <SeparatorVertical $margin={8} />
        <FullGridContainer>
          <Input
            type="text"
            name="address"
            label={t('common:address')}
            placeholder={t('common:address')}
            required
            fontCase="TitleCase"
          />
        </FullGridContainer>
      </Card>

      <SeparatorVertical $margin={24} />
      <Card withBorder radius={8}>
        <Text variant="headingXSmall">{t('cruise:contact_person')}</Text>
        <SeparatorVertical $margin={8} />
        <OneQuarterGridContainer>
          <Input
            type="text"
            name="contact.name"
            label={t('common:name')}
            placeholder={t('common:name')}
            required
          />
          <Input
            type="text"
            name="contact.email"
            label={t('common:email')}
            placeholder={t('common:email')}
            required
          />
          <Input
            type="text"
            name="contact.phone"
            label={t('cruise:tel_phone')}
            placeholder={t('cruise:tel_phone')}
            required
          />
          <Input
            type="text"
            name="contact.description"
            label={t('common:description')}
            placeholder={t('common:description')}
            required
          />
        </OneQuarterGridContainer>
      </Card>
    </UniskaiFormTemplate>
  );
}
